if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector('.product-form__submit-label') || this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // PDP Builder: add selected flavors only, skip the page product
        if (document.querySelector('bundle-products')) {
          if (!window.BundleBuilder?.getCartPayload) {
            this.handleErrorMessage('Bundle builder is not ready');
            return;
          }
          this.onBundleBuilderSubmit(evt);
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Add painpoint as cart attribute if exists
        const painpoint = window.getPainpoint && window.getPainpoint();
        if (painpoint) {
          formData.append('attributes[painpoint]', painpoint);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              return this.handleCartAddSuccess(response, formData.get('id'), evt);
            }

            // Prefer a fresh section render; never pass a cart.js payload (no sections) into renderContents.
            if (typeof window.renderCartDrawer === 'function' && this.cart?.tagName === 'CART-DRAWER') {
              await window.GiftsEngine?.flush();
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                drawerHandled: true,
                productVariantId: formData.get('id'),
                cartData: response,
              });
              await window.renderCartDrawer({ open: true });
              return;
            }

            return this.handleCartAddSuccess(response, formData.get('id'), evt);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.resetSubmitButton(evt);
          });
      }

      cartAddJsUrl() {
        const base = window.routes?.cart_add_url || '/cart/add';
        return base.endsWith('.js') ? base : `${base}.js`;
      }

      storefrontCartAdd(body) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 10000);

        return fetch(this.cartAddJsUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        })
          .then(async (response) => {
            let data = {};
            try {
              data = await response.json();
            } catch (error) {
              data = {};
            }
            const status = Number(data.status) || response.status;
            if (!response.ok || status >= 400) {
              const err = new Error(data.description || data.message || 'Could not add items to cart');
              err.status = status;
              err.description = data.description || data.message || err.message;
              err.message = data.message || err.message;
              err.errors = data.errors;
              throw err;
            }
            return data;
          })
          .catch((error) => {
            if (error && (error.status || error.description)) throw error;
            const timedOut = error && error.name === 'AbortError';
            const err = new Error(timedOut ? 'Cart add timed out' : 'Could not add items to cart');
            err.status = timedOut ? 408 : 0;
            err.description = err.message;
            throw err;
          })
          .finally(() => clearTimeout(timer));
      }

      openThemeCartDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        drawer.classList.remove('is-empty');
        drawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
        if (typeof drawer.open === 'function') drawer.open(document.activeElement);
      }

      cartAddFailed(response) {
        return !response || Boolean(response.status) || (!response.key && !response.id && !response.items);
      }

      onBundleBuilderSubmit(evt) {
        this.handleErrorMessage();

        const payload = window.BundleBuilder.getCartPayload({ form: this.form });

        if (!payload.items.length) {
          this.handleErrorMessage('SELECT AT LEAST 1 BAG');
          return;
        }

        console.log('[BundleBuilder] submitting to cart', payload);

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        const painpoint = window.getPainpoint && window.getPainpoint();
        const sectionIds = this.cart?.getSectionsToRender?.().map((section) => section.id) || [
          'cart-drawer',
          'cart-icon-bubble',
        ];
        const willRenderDrawer =
          typeof window.renderCartDrawer === 'function' && this.cart?.tagName === 'CART-DRAWER';

        const items = payload.items.map((entry) => {
          const item = { id: Number(entry.id), quantity: Number(entry.quantity) || 1 };
          if (entry.selling_plan) item.selling_plan = entry.selling_plan;
          if (entry.properties) item.properties = { ...entry.properties };

          return item;
        });

        const handleAddError = (response) => {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: items[0]?.id,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description || response.message || 'Could not add items to cart');
          this.error = true;
        };

        (async () => {
          const startedAt = performance.now();
          try {
            const withMeta = (body, withSections) => {
              const next = { ...body };
              if (painpoint) next.attributes = { painpoint };
              if (withSections) {
                next.sections = sectionIds;
                next.sections_url = window.location.pathname;
              }
              return next;
            };

            // Submit the whole selection together. Splitting off the first flavor
            // leaves a partial bundle when the follow-up request fails or times out.
            const lastResponse = await this.storefrontCartAdd(withMeta({ items }, true));
            if (this.cartAddFailed(lastResponse)) {
              handleAddError(lastResponse);
              return;
            }

            if (typeof window.GiftsEngine?.flush === 'function') {
              await Promise.resolve(window.GiftsEngine.flush()).catch(() => {});
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form-builder',
              drawerHandled: willRenderDrawer,
              productVariantId: items[0]?.id,
              cartData: lastResponse,
            });

            // The gift flush can change lines and replace the drawer. Add-response
            // sections predate those changes, so fetch the final server sections.
            if (willRenderDrawer) {
              await window.renderCartDrawer({ open: true });
            } else if (lastResponse.sections && this.cart?.renderContents) {
              try {
                this.cart.renderContents(lastResponse);
              } catch (renderError) {
                console.warn('[BundleBuilder] renderContents failed', renderError);
              }
            }

            console.log('[BundleBuilder] ATC done', Math.round(performance.now() - startedAt), 'ms');
          } catch (e) {
            console.error('[BundleBuilder] cart add failed', e);
            handleAddError({
              status: e.status || 500,
              description: e.description || e.message || 'Could not add items to cart',
              message: e.message,
              errors: e.errors,
            });
          } finally {
            this.resetSubmitButton(evt);
          }
        })();
      }

      handleCartAddSuccess(response, productVariantId, evt) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
              const submitButtonText = this.getSubmitButtonText();
          this.submitButton.setAttribute('aria-disabled', true);
          submitButtonText?.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        }

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId,
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
        }
      }

      resetSubmitButton(evt) {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner')?.classList.add('hidden');

        if (evt) CartPerformance.measureFromEvent('add:user-action', evt);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const submitButtonText = this.getSubmitButtonText();
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && submitButtonText) submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (submitButtonText) submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get cart() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      getSubmitButtonText() {
        return this.submitButton.querySelector('.product-form__submit-label') || this.submitButton.querySelector('span');
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
